import HBMessage from "@/components/HBMessage";
import HBModal from "@/components/HBModal";
import ModalWrap from "@/components/HBModal/ModalWrap";
import useAppStore from "@/context/store";
import {
  checkVipIsCorrect,
  getMaterialCorrectionTask,
  materialCorrection,
  UpgradeType,
} from "@/services/gaoding";
import { getAccessToken, isUnifiedUser } from "@/utils/auth";
import { isGdMerged } from "@/utils/gaoding";
import { globalDialogControl } from "@/utils/globalDialog";
import { openUdesk } from "@/utils/initUdesk";
import useReq from "@/utils/useReq";
import { useMemoizedFn } from "ahooks";
import { Button, ModalFuncProps } from "antd";
import { useRouter } from "next/router";
import { useMemo, useState } from "react";
import styles from "./index.module.less";

// 需求文档：https://doc.huanleguang.com/pages/viewpage.action?pageId=392314229

function useTask({
  onOk,
  onCancel,
}: {
  onOk?: () => void;
  onCancel?: () => void;
}) {
  const [currentTask, setCurrentTask] = useState(null);

  const { run: loopTask, cancel: cancelLoop } = useReq(
    async () => {
      if (currentTask) {
        const res = await getMaterialCorrectionTask(currentTask);

        enum Status {
          SUCCESS = "SUCCESS",
          PROCESSING = "PROCESSING",
          FAIL = "FAIL",
        }

        if (res.status === Status.PROCESSING) {
          setCurrentTask(res.id);
        } else if (res.status === Status.SUCCESS) {
          HBMessage.success("升级成功");
          onOk?.();
          cancelLoop();
          location.reload();
        } else if (res.status === Status.FAIL) {
          HBMessage.error("升级失败，请联系客服");
          onCancel?.();
          cancelLoop();
        }
      }
    },
    {
      manual: true,
      pollingInterval: 3000,
      pollingWhenHidden: true,
      pollingErrorRetryCount: 3,
      retryCount: 1,
    }
  );

  const runTask = useMemoizedFn(async () => {
    const res = await materialCorrection();
    if (res.id) {
      setCurrentTask(res.id);
      loopTask();
    }
  });

  return { runTask, currentTask };
}

export const VipUpgradeModal = ModalWrap(
  function Raw(props: ModalFuncProps & { upgradeType: UpgradeType }) {
    const { runTask, currentTask } = useTask({
      onOk() {
        props.onOk?.();
      },
    });

    const { upgradeType, ...rest } = props;

    const content = useMemo(() => {
      if (upgradeType === UpgradeType.MATERIAL_PERSONAL_TO_ORG) {
        return (
          <div className={styles.content}>
            <p>
              尊敬的用户，我司将于8月进行会员体系升级，由于您当前花瓣素材会员等级过低，为了保障您的使用体验，您可免费升级到
              “<strong>素材企业会员</strong>”。我司将在您升级成功后
              <strong>额外发放1个月素材企业会员</strong>
              作为补偿，感谢您的理解与支持！
            </p>
            <ul>
              <li>
                升级后的“<strong>花瓣素材企业会员</strong>
                ”时长与当前会员剩余时长一致，权益内容大于当前会员
              </li>
              <li>
                额外会员补偿将通过 <strong>站内信</strong>{" "}
                下发兑换码，领取成功后通过会员兑换页进行兑换。
              </li>
            </ul>
          </div>
        );
      } else if (upgradeType === UpgradeType.FREE_PERSONAL_TO_ORG_FREE) {
        return (
          <div className={styles.content}>
            <p>
              尊敬的用户，我司将于8月进行会员体系升级，由于您当前租户类型与素材会员冲突，为保障您的使用体验，您可免费升级到团队版。我司将在您升级成功后
              <strong>额外发放1个月模板设计团队版会员作为补偿</strong>
              ，感谢您的理解与支持！
            </p>
            <ul>
              <li>
                额外会员补偿将通过 <strong>站内信</strong>
                下发兑换码，领取成功后通过会员兑换页进行兑换。
              </li>
            </ul>
          </div>
        );
      } else if (upgradeType === UpgradeType.DESIGN_PERSONAL_TO_ORG) {
        return (
          <div className={styles.content}>
            <p>
              尊敬的用户，我司将于8月进行会员体系升级，由于您当前模板设计会员等级过低，为了保障您的使用体验，您可免费升级到
              <strong>“模板设计团队版会员”</strong>。我司将在您升级成功后
              <strong>额外发放3个月模板设计团队版会员</strong>
              作为补偿，感谢您的理解与支持！
            </p>
            <ul>
              <li>
                升级后的“<strong>模板设计团队版会员</strong>
                ”时长与当前会员剩余时长一致，权益内容大于当前会员
              </li>
              <li>
                额外会员补偿将通过 <strong>站内信</strong>
                下发兑换码，领取成功后通过会员兑换页进行兑换。
              </li>
            </ul>
          </div>
        );
      }
    }, [upgradeType]);

    return (
      <HBModal
        {...rest}
        width={currentTask ? 352 : 380}
        footer={null}
        closable={false}
        maskClosable={false}
        keyboard={false}
        className={styles.dialog}
      >
        {!currentTask ? (
          <div className={styles.info}>
            <div className={styles.title}>升级通知</div>
            {content}

            <div className={styles.footer}>
              <Button type="primary" onClick={runTask}>
                免费升级
              </Button>
              <Button type="link" onClick={openUdesk}>
                联系客服
              </Button>
            </div>
          </div>
        ) : (
          <div className={styles.upgrade}>
            <div className={styles.vipIcon}>
              <div className={styles.bg}></div>
            </div>
            <div className={styles.title}>会员升级中</div>
            <div className={styles.desc}>感谢你的等待</div>
            <div className={styles.footer}>
              <Button type="link" onClick={openUdesk}>
                联系客服
              </Button>
            </div>
          </div>
        )}
      </HBModal>
    );
  },
  { unique: true }
);

export function useCheckVipUpgrade() {
  const { vips, user } = useAppStore((s) => ({ vips: s.vips, user: s.user }));
  const router = useRouter();

  useReq(
    async () => {
      if (
        [
          "/oauth/authorize",
          "/login",
          "/signup",
          "/bind/tel",
          "/oauth/callback",
          "/share",
        ].includes(router.pathname)
      ) {
        // 授权页面不弹
        return;
      }

      const accessToken = await getAccessToken();

      if (
        isGdMerged(user) &&
        vips.length > 0 &&
        !isUnifiedUser(user?.user_id, accessToken)
      ) {
        const checkRes = await checkVipIsCorrect();

        if (checkRes.type && checkRes.type !== UpgradeType.IGNORE) {
          globalDialogControl.push({
            key: "VipUpgradeModal",
            dialog: VipUpgradeModal,
            props: {
              upgradeType: checkRes.type,
            },
            // 优先级最高
            priority: -1,
          });
        }
      }
    },
    {
      refreshDeps: [user?.user_id, vips],
    }
  );
}
